/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
 */

import {
    NgZone,
    Component,
    ViewChild,
    OnInit,
    ElementRef,
    AfterViewInit,
    ViewEncapsulation
} from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import WebViewer, { WebViewerInstance } from '@pdftron/webviewer';
import {
    FcResponsiveAgDialogComponent,
    FcResponsiveAgDialogService,
    FcAgLoadingMaskService,
    FcAgContextService
} from 'ag-libraries';
import * as _ from 'lodash';
import { AppService } from '../app.service';
import { 
    FcAddMoreDocumentFileFormComponent
} from '../fc-add-more-document-file-form/fc-add-more-document-file-form.component';
import { SortByCasePipe, SortByPipe } from '../app.pipe';


/**
 * FC Preview Document File Form component.
 */
@Component({
    selector: 'fc-preview-document-file-form',
    templateUrl: './fc-preview-document-file-form.component.html',
    styleUrls: ['./fc-preview-document-file-form.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class FcPreviewDocumentFileFormComponent
    extends FcResponsiveAgDialogComponent
    implements OnInit, AfterViewInit
{
    /**
     * WebViewer element.
     */
    @ViewChild('viewer', { static: false }) viewer: ElementRef;

    /**
     * Load document status.
     */
    loadDocumentStatus = '';

    /**
     * Dialog data.
     */
    dialogData: any;

    /**
     * Grid options.
     */
    gridOptions: any;

    /**
     * Instance.
     */
    instance: any;

    /**
     * Document id.
     */
    documentId: any;

    /**
     * Validate event start.
     */
    isSubmitted: boolean = false;

    /**
     * No document message.
     */
    noDocumentMessage: string;

    /**
     * File name.
     */
    fileName: string;

    /**
     * PDFTron license key.
     */
    licenseKey: string = '';

    /**
     * Document attributes.
     */
    attributes: any = {};

    /**
     * Auto expand attribute panel.
     */
    expandAttributePanel: boolean = false;

    /**
     * Selected document index.
     */
    selectedDocIndex: number = 0;

    /**
     * Selected case index.
     */
    selectedCaseIndex: number = 0;

    /**
     * Previous selected case Index.
     */
    preSelectedCaseIndex: number = 0;

    /**
     * Grid state from PDUV app.
     */
    gridStatePduv: any;

    /**
     * Preview document list.
     */
    showDocuments: any;

    /**
     * Check if document is from bulk ack action.
     */
    isFromBulkAck: boolean = false;

    /**
     * Check if document is from bulk ack action.
     */
    isEnableBulkAck: boolean = false;

    /**
     * Check is document is pegasus case.
     */
    isPegasus: boolean = false;

    /**
     * Check is single pegasus case.
     */
    isSingleCase: boolean = false;

    /**
     * Check is doc info that is support detect a document is acknowledged or did not.
     */
    isAcknowledgedDoc: boolean = false;

    /**
     * Languages.
     */
    languages: any;

    /**
     * SelectCase.
     */
    selectCase: any;
     
    /**
     * Doc url.
     */
    docUrl: string;

    /**
     * DefaultDoc url.
     */
    defaultDoc: any = {};

    /**
     * Document Ids.
     */
    documentIds: any = [];

    /**
     * Suffix Without Record.
     */
    suffixWithoutRecord: string = '?recordDownload=false';

    /**
     * Constructor.
     *
     * @param fcResponsiveAgDialogService FcResponsiveAgDialogService.
     * @param fcAgLoadingMaskService FcAgLoadingMaskService.
     * @param fcAgContextService FcAgContextService.
     * @param zone Zone.
     * @param appService AppService.
     */
    constructor(
        private fcResponsiveAgDialogService: FcResponsiveAgDialogService,
        private fcAgLoadingMaskService: FcAgLoadingMaskService,
        private fcAgContextService: FcAgContextService,
        private zone: NgZone,
        private appService: AppService
    ) {
        super();
    }

    /**
     * Angular OnInit lifecycle hook.
     */
    ngOnInit() {
        this.dialogData = (<any> this).data;
        this.licenseKey = (<any> this).licenseKey;
        this.isPegasus = this.dialogData?.pegasus?.isPegasus;
        this.isSingleCase = this.dialogData?.pegasus?.isSingleCase;
        this.isEnableBulkAck = this.dialogData.requestParams.isEnableBulkAck;
        this.languages = this.dialogData?.languages;
        // document cases
        if (this.isPegasus) {
            this.showDocuments = this.appService.parseDocumentCases(this.dialogData?.documents);
            this.isFromBulkAck = this.isSingleCase ? false : true;
            if (this.isSingleCase) {
                const docCases = this.sortDocumentCase(this.showDocuments);
                const docsInCase = this.sortDocuments(docCases[0]);
                this.selectedDocIndex = this.appService.findSelectedDocumentForSingleCaaseIndex(docsInCase);
                this.attributes = _.get(docsInCase, `${this.selectedDocIndex}`, {});
            } else {
                this.reasignShowDocument(this.showDocuments, true);
            }
        }
        // standard documents
        else {
            this.showDocuments = this.appService.parseStandardDocumentDetails(this.dialogData.documents);
            const docs = this.sortDocuments(this.showDocuments);
            // this.attributes = _.get(this, 'showDocuments[0]', {});
            this.attributes = _.get(docs, 0, {});
            this.isFromBulkAck = this.showDocuments[0]?.isFromBulkAck;
        }
        this.loadDocumentStatus = this.showDocuments?.length
            ? 'IN_PROGRESS'
            : 'ERROR';
        this.noDocumentMessage = 'No Document Available';
    }

    /**
     * AfterViewInit lifecycle hook.
     */
    ngAfterViewInit() {
        let initObj: any = {
            path: '/document-repository-ui/document-repository-ag-ui/libraries/pdftron',
            disabledElements: [
                'ribbons',
                'toolsHeader',
                'menuButton',
                'contextMenuPopup',
                'toggleNotesButton',
                'searchButton',
                'selectToolButton',
                'panToolButton',
                'viewControlsButton',
                'thumbRotateCounterClockwise',
                'thumbRotateClockwise',
                'pageManipulationOverlay',
                'toolsOverlay'
            ]
        };

        if (this.licenseKey) {
            initObj.licenseKey = this.licenseKey;
        }

        if (this.viewer) {
            WebViewer(initObj, this.viewer.nativeElement).then(
                (instance: any) => {
                    this.instance = instance;
                    instance.UI.addEventListener(
                        instance.UI.Events.LOAD_ERROR,
                        () => {
                            this.loadDocumentStatus = 'ERROR';
                            instance.UI.closeDocument();
                        }
                    );
                    instance.UI.addEventListener(
                        instance.UI.Events.DOCUMENT_LOADED,
                        () => {
                            instance.UI.setZoomLevel(1.3);
                        }
                    );
                    if (this.showDocuments) {
                        this.showDocument(instance, {isFirstOpen: true, indexDoc: 0});
                    }
                }
            );
        }
    }

    /**
     * Load document inside WebViewer UI.
     *
     * @param instance WebViewerInstance.
     * @param wrapperData WrapperData.
     * @param fileUrl FileUrl.
     */
    showDocument(instance: WebViewerInstance, wrapperData?: any, fileUrl?: string) {
        let showFileUrl;
        let fileName: string;
        let isFirstOpen: boolean = wrapperData.isFirstOpen;
        let indexDoc: number = wrapperData.indexDoc;
        if (this.isPegasus) {
            const documentCaseUrl = this.isSingleCase ?
                _.get(this, `showDocuments[0][${this.selectedDocIndex}].url`, '') :
                _.get(this, `showDocuments[0][${indexDoc}].url`, '');
            showFileUrl = fileUrl ? fileUrl : documentCaseUrl;
            this.docUrl = showFileUrl;
            if (isFirstOpen) {
                let doc = this.showDocuments[0].filter((doc: any) => {
                    if (doc.selectedFromPDUV) {
                        return doc;
                    }
                });
                fileName = doc[0] ? doc[0]?.fileName : _.get(
                    this, `showDocuments[0][${this.selectedDocIndex}].fileName`, '');
            } else {
                fileName =  _.get(this, `showDocuments[0][${indexDoc}].fileName`, '');
            }
            this.fileName = fileName;
        } else {
            const standardDocUrl = _.get(this, `showDocuments[${indexDoc}].url`, '');
            showFileUrl = fileUrl ? fileUrl : standardDocUrl;
            this.docUrl = showFileUrl;
            fileName =  _.get(this, `showDocuments[${indexDoc}].fileName`, '');
            this.fileName = fileName;
        }
        
        this.appService.downloadDocumentFile(showFileUrl  + this.suffixWithoutRecord).subscribe(
            (response: HttpResponse<any>) => {
                const blobFile = response.body;
                const headers = response.headers;
                const fileExtension = this.appService.getFileExtension(
                    fileName
                );
                if (this.appService.isSupportedFileType(fileName)) {
                    this.loadDocumentStatus = 'SUCCESS';
                    instance.UI.loadDocument(blobFile, {
                        filename: fileName,
                        extension: fileExtension,
                        officeOptions: {
                            formatOptions: {
                                excelDefaultCellBorderWidth: 0.5
                            }
                        }
                    });
                } else {
                    this.loadDocumentStatus = 'ERROR';
                    this.noDocumentMessage = 'This file cannot be loaded in the viewer.';
                    this.downloadFile(blobFile, fileName);
                }
            },
            (error: any) => {
                console.log(error);
                this.loadDocumentStatus = 'ERROR';
            }
        );
    }

    /**
     * Load selected document.
     *
     * @param indexDocument IndexDocument.
     * @param indexCase IndexCase.
     */
    loadSelectDocument(indexDocument: number, indexCase?: any) {
        let docUrl;
        if (this.isPegasus) {
            docUrl = _.get(
                this, `showDocuments[${indexCase}][${indexDocument}].url`, ''
            );
            this.showDocument(this.instance, {isFirstOpen: false, indexDoc: indexDocument}, docUrl);
            this.selectedDocIndex = indexDocument;
            this.selectedCaseIndex = indexCase;
            this.attributes = _.get(
                this, `showDocuments[${indexCase}][${indexDocument}]`, {}
            );
            this.docUrl = docUrl;
            this.fileName = this.attributes?.fileName;
        } else {
            docUrl = _.get(
                this, `showDocuments[${indexDocument}].url`, ''
            );
            this.showDocument(this.instance, {isFirstOpen: false, indexDoc: indexDocument}, docUrl);
            this.selectedDocIndex = indexDocument;
            this.attributes = _.get(
                this, `showDocuments[${indexDocument}]`, {}
            );
            this.fileName = this.attributes?.fileName;
            this.docUrl = docUrl;
        }

    }

    /**
     * Close dialog.
     *
     */
    closeDialog() {
        this.appService.windowRef.FC.utils.triggerListener(
            'show-document-shared-ag-ui-closed', { btn: 'closed', action: 'closed' }
        );
        this.revertDocumentOrDocumentCaseState(this.isPegasus);
        super.close();
    }

    /**
     * Acknowledge docs.
     *
     */
    btnAcknowledge() {
        this.appService.windowRef.FC.utils.triggerListener(
            'show-document-shared-ag-ui-acknowledge', 
            { btn: 'closed', action: 'acknowledge', documents: this.showDocuments, isPegasus: this.isPegasus }
        );
        this.revertDocumentOrDocumentCaseState(this.isPegasus);
        super.close(true);
    }

    /**
     * Add more documents.
     *
     */
    addMoreDocuments() {
        this.fcResponsiveAgDialogService
            .addModal(
                FcAddMoreDocumentFileFormComponent,
                {
                    data: {
                        documents: this.showDocuments,
                        gridStatePduv: this.dialogData.gridStatePduv,
                        requestParams: this.dialogData.requestParams,
                        pegasus: this.dialogData.pegasus
                    }
                },
                {
                    wrapperClass: 'add-more-document-dialog-wrapper'
                }
            )
            .subscribe((isSubmitted: boolean) => {
                if (isSubmitted) {
                    if (this.isPegasus) {
                        var documentList = this.appService.getDocumentList();
                        documentList.forEach((newCases: any) => {
                            let matchingGroup;
                            if (this.attributes?.categoryId === 'pegasus_safety_letter') {
                                matchingGroup = this.showDocuments.find((showCases: any) =>
                                    showCases[0].caseNumber === newCases[0].caseNumber &&
                                    showCases[0].caseVersion === newCases[0].caseVersion
                                );
                            } else if (this.attributes?.categoryId === 'pegasus_line_listing') {
                                matchingGroup = this.showDocuments.find((showCases: any) =>
                                    showCases[0].srCaseID === newCases[0].srCaseID
                                );
                            }
                            
                            if (matchingGroup) {
                                matchingGroup.push(...newCases);
                            } else {
                                this.showDocuments.push(newCases);
                            }
                        });
                        this.reasignShowDocument(this.showDocuments, true);
                        this.selectedCaseIndex = 0;
                        this.selectedDocIndex = 0;
                    } else {
                        this.showDocuments = [...this.showDocuments, ...this.appService.getDocumentList()];
                        this.reasignShowDocument(this.showDocuments, false);
                        this.selectedDocIndex = 0;
                    }
                }
            });
    }

    /**
     * Download file.
     *
     * @param blobFile BlobFile.
     * @param fileName FileName.
     */
    downloadFile(blobFile: any, fileName: string) {
        const templateFileUrl = window.URL.createObjectURL(blobFile);
        const link = document.createElement('a');
        link.href = templateFileUrl;
        link.download = fileName;
        link.click();
    }

    /**
     * Check is show ack button.
     *
     * @returns Boolean.
     */
    isShowAcknowledgeBtn() {
        if (this.isFromBulkAck) {
            return true;
        }
        // from title/view document
        else {
            return this.appService.isAbleToAck(this.showDocuments, this.isPegasus);
        }
    }

    /**
     * Translate language based on language code.
     *
     * @param languageCode LanguageCode.
     * @returns Langauge.
     */
    translateLanguage(languageCode: string) {
        return this.languages?.[languageCode]?.comboDisplay;
    }

    /**
     * Is Allow to Load Document when user expand or collapse document case.
     * 
     * @param selectedCaseIndex SelectedCaseIndex.
     * @param preSelectedCaseIndex PreSelectedCaseIndex.
     * @returns True or False.
     */
    isAllowToLoadDocument(selectedCaseIndex: number, preSelectedCaseIndex: number) {
        return selectedCaseIndex ===  preSelectedCaseIndex;
    }

    /**
     * Download single doc.
     * 
     * @param item Item.
     * @param isFromView IsFromView.
     */
    downloadSingleDoc(item: any, isFromView: boolean) {
        const fileName = item?.fileName;
        let url =  isFromView ? this.docUrl + this.suffixWithoutRecord : item?.url + this.suffixWithoutRecord;
        this.appService.downloadDocumentFile(url).subscribe(
            (response: HttpResponse<any>) => {
                const blobFile = response.body;
                this.downloadFile(blobFile, fileName);
            },
            (error: any) => {
                console.log(error);
                this.loadDocumentStatus = 'ERROR';
            });
    }

    /**
     * Revert Document Pks State.
     */
    revertDocumentPksState() {
        this.appService.setDocumentPks([]);
    }

    /**
     * Revert Document Cases State.
     */
    revertDocumentCasesState() {
        this.appService.setDocumentCases([]);
    }

    /**
     * Revert Document Cases State.
     */
    revertDocumentCasesKeyState() {
        this.appService.setDocumentCasesKey([]);
    }

    /**
     * Revert Document And Document Case State.
     * 
     * @param isPegas IsPegas.
     */
    revertDocumentOrDocumentCaseState(isPegas: boolean) {
        this.revertDocumentPksState();
        if (isPegas) {
            this.revertDocumentCasesState();
            this.revertDocumentCasesKeyState();
        }
    }

    /**
     * Reasign Show Document.
     * 
     * @param listDoc ListDoc.
     * @param isPegasus IsPegasus.
     */
    reasignShowDocument(listDoc: any, isPegasus: boolean) {
        const sortByPipe = isPegasus ? new SortByCasePipe() : new SortByPipe();
        const templatedata = sortByPipe.transform(
            listDoc, 'asc', isPegasus ? 'caseNumber' : 'title');
        this.attributes = _.get(templatedata, isPegasus ? '[0][0]' : '[0]', {});
        this.fileName =  this.attributes?.fileName;
        this.showDocument(this.instance, {isFirstOpen: false, indexDoc: 0}, this.attributes?.url);
    }

    /**
     * Sort Documents.
     * 
     * @param docs Provide list of document.
     * @returns Documents was sorted.
     */
    sortDocuments(docs: any) {
        const sortByPipe = new SortByPipe();
        return sortByPipe.transform(
            docs, 'asc', 'title');
    }

    /**
     * Sort Document Case.
     * 
     * @param docCases Provide list of document cases.
     * @returns Documents was sorted.
     */
    sortDocumentCase(docCases: any) {
        const sortByPipe = new SortByCasePipe();
        return sortByPipe.transform(
            docCases, 'asc', 'caseNumber');
    }

    /**
     * Toggle doc case item event.
     * 
     * @param event Accordion data.
     * @param selectedCaseIndex SelectedCaseIndex.
     */
    toggleDocCaseItem(event: any, selectedCaseIndex: number) {
        this.selectCase = event.name;
        this.preSelectedCaseIndex = Number(selectedCaseIndex);
        if (!this.isAllowToLoadDocument(this.selectedCaseIndex, this.preSelectedCaseIndex)) {
            this.selectedDocIndex = this.appService.findSelectedDocIndex(this.showDocuments);
            this.loadSelectDocument(this.selectedDocIndex, selectedCaseIndex);
        }
        this.selectedCaseIndex = this.preSelectedCaseIndex;
        this.preSelectedCaseIndex = 0;
    }
}
